import React, { useEffect, useState, useCallback, useMemo } from 'react';
import axios from 'axios';
import {
  TableContainer, Paper, Table, TableHead, TableRow, TableCell, TableBody,
  TableFooter, TextField, Checkbox, Select, MenuItem, FormControl, InputLabel,
  Stack, Typography, Chip, Card, CardContent, Tooltip, IconButton, Button,
  Box,
} from '@mui/material';

const API_BASE = 'https://portal.peaksoftcon.com:8081/api';

const TR_MONTHS = [
  'Ocak', 'Şubat', 'Mart', 'Nisan', 'Mayıs', 'Haziran',
  'Temmuz', 'Ağustos', 'Eylül', 'Ekim', 'Kasım', 'Aralık',
];

const fmtTL = (n) => {
  const v = Number(n) || 0;
  return v.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' ₺';
};

const todayISO = () => new Date().toISOString().slice(0, 10);

function AdminCompanyPayments() {
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [rows, setRows] = useState([]);
  const [drafts, setDrafts] = useState({});

  const fetchRows = useCallback(() => {
    axios
      .get(`${API_BASE}/companyPayments`, { params: { year, month } })
      .then((res) => {
        setRows(res.data || []);
        const d = {};
        (res.data || []).forEach((r) => {
          d[r.id] = {
            amount: r.amount ?? '',
            due_date: r.due_date ? r.due_date.slice(0, 10) : '',
            paid_date: r.paid_date ? r.paid_date.slice(0, 10) : '',
            invoice_number: r.invoice_number ?? '',
            notlar: r.notlar ?? '',
          };
        });
        setDrafts(d);
      })
      .catch((err) => console.error('Error fetching payments:', err));
  }, [year, month]);

  useEffect(() => { fetchRows(); }, [fetchRows]);

  const updateRow = (id, patch) => {
    axios
      .put(`${API_BASE}/companyPayments/${id}`, patch)
      .then(() => {
        setRows((prev) => prev.map((r) => (r.id === id ? { ...r, ...patch } : r)));
      })
      .catch((err) => console.error('Error updating payment:', err));
  };

  const handleDraftChange = (id, key, value) => {
    setDrafts((prev) => ({ ...prev, [id]: { ...prev[id], [key]: value } }));
  };

  const flushDraft = (row, key, transform = (v) => v) => {
    const draftVal = transform(drafts[row.id]?.[key] ?? '');
    const current = row[key] ?? '';
    if (String(draftVal ?? '') === String(current ?? '')) return;
    updateRow(row.id, { [key]: draftVal });
  };

  const handlePaidToggle = (row) => {
    const next = row.paid ? 0 : 1;
    const patch = { paid: next };
    if (next && !row.paid_date) {
      patch.paid_date = todayISO();
      setDrafts((prev) => ({
        ...prev,
        [row.id]: { ...prev[row.id], paid_date: todayISO() },
      }));
    }
    updateRow(row.id, patch);
  };

  const handleInvoiceToggle = (row) => {
    updateRow(row.id, { invoice_issued: row.invoice_issued ? 0 : 1 });
  };

  const copyFromPrevious = (row) => {
    axios
      .post(`${API_BASE}/companyPayments/${row.id}/copyFromPrevious`)
      .then((res) => {
        const newAmt = res.data?.amount;
        if (newAmt != null) {
          setRows((prev) => prev.map((r) => (r.id === row.id ? { ...r, amount: newAmt } : r)));
          setDrafts((prev) => ({
            ...prev,
            [row.id]: { ...prev[row.id], amount: newAmt },
          }));
        }
      })
      .catch((err) => console.error('Error copying previous amount:', err));
  };

  // ── Status derivation ───────────────────────────────────────
  // paid → "Ödendi" (green)
  // !paid && due_date < today → "Gecikmiş" (red)
  // !paid && due_date >= today → "Bekliyor" (yellow)
  // !paid && no due_date → "Bekliyor" (yellow)
  const statusOf = (row) => {
    if (row.paid) return { label: 'Ödendi', color: 'success' };
    if (row.due_date) {
      const due = row.due_date.slice(0, 10);
      if (due < todayISO()) return { label: 'Gecikmiş', color: 'error' };
    }
    return { label: 'Bekliyor', color: 'warning' };
  };

  // ── KPIs (sum across visible rows) ──────────────────────────
  const kpis = useMemo(() => {
    let expected = 0, collected = 0, pending = 0, noInvoice = 0;
    rows.forEach((r) => {
      const amt = Number(r.amount) || 0;
      expected += amt;
      if (r.paid) collected += amt;
      else pending += amt;
      if (!r.invoice_issued) noInvoice += amt;
    });
    return { expected, collected, pending, noInvoice };
  }, [rows]);

  const yearOptions = [];
  const cy = now.getFullYear();
  for (let y = cy - 2; y <= cy + 1; y++) yearOptions.push(y);

  return (
    <Box>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        justifyContent="space-between"
        alignItems={{ xs: 'stretch', sm: 'center' }}
        spacing={2}
        sx={{ mb: 2 }}
      >
        <Typography variant="h4" sx={{ m: 0 }}>Firma Ödemeleri</Typography>
        <Stack direction="row" spacing={2}>
          <FormControl size="small" sx={{ minWidth: 140, flex: 1 }}>
            <InputLabel>Ay</InputLabel>
            <Select label="Ay" value={month} onChange={(e) => setMonth(e.target.value)}>
              {TR_MONTHS.map((m, i) => (
                <MenuItem key={m} value={i + 1}>{m}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 100, flex: 1 }}>
            <InputLabel>Yıl</InputLabel>
            <Select label="Yıl" value={year} onChange={(e) => setYear(e.target.value)}>
              {yearOptions.map((y) => (
                <MenuItem key={y} value={y}>{y}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Stack>
      </Stack>

      {/* KPI cards */}
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ mb: 2 }}>
        <KpiCard title="Toplam Beklenen" value={kpis.expected} />
        <KpiCard title="Tahsil Edilen"   value={kpis.collected} accent="success" />
        <KpiCard title="Bekleyen Tutar"  value={kpis.pending}   accent="warning" />
        <KpiCard title="Faturasız"       value={kpis.noInvoice} accent="error" />
      </Stack>

      <TableContainer component={Paper} sx={{ overflowX: 'auto' }}>
        <Table size="small" sx={{ minWidth: 1100 }}>
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 'bold', minWidth: 160 }}>Firma Adı</TableCell>
              <TableCell sx={{ fontWeight: 'bold', minWidth: 120 }}>Tutar (₺)</TableCell>
              <TableCell sx={{ fontWeight: 'bold', minWidth: 130 }}>Vade Tarihi</TableCell>
              <TableCell sx={{ fontWeight: 'bold', textAlign: 'center' }}>Ödendi</TableCell>
              <TableCell sx={{ fontWeight: 'bold', minWidth: 130 }}>Ödeme Tarihi</TableCell>
              <TableCell sx={{ fontWeight: 'bold', textAlign: 'center' }}>Fatura</TableCell>
              <TableCell sx={{ fontWeight: 'bold', minWidth: 130 }}>Fatura No</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Durum</TableCell>
              <TableCell sx={{ fontWeight: 'bold', minWidth: 180 }}>Notlar</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row) => {
              const st = statusOf(row);
              const draft = drafts[row.id] || {};
              return (
                <TableRow key={row.id} hover>
                  <TableCell sx={{ fontWeight: 500 }}>{row.company_name}</TableCell>

                  <TableCell>
                    <Stack direction="row" spacing={0.5} alignItems="center">
                      <TextField
                        size="small"
                        type="number"
                        inputProps={{ step: '0.01', min: 0, style: { textAlign: 'right' } }}
                        value={draft.amount ?? ''}
                        onChange={(e) => handleDraftChange(row.id, 'amount', e.target.value)}
                        onBlur={() => flushDraft(row, 'amount', (v) => (v === '' ? 0 : Number(v)))}
                        sx={{ width: 110 }}
                      />
                      <Tooltip title="Önceki aydan tutarı kopyala">
                        <IconButton size="small" onClick={() => copyFromPrevious(row)}>
                          ↺
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  </TableCell>

                  <TableCell>
                    <TextField
                      size="small"
                      type="date"
                      value={draft.due_date ?? ''}
                      onChange={(e) => handleDraftChange(row.id, 'due_date', e.target.value)}
                      onBlur={() => flushDraft(row, 'due_date', (v) => v || null)}
                      sx={{ width: 150 }}
                    />
                  </TableCell>

                  <TableCell sx={{ textAlign: 'center' }}>
                    <Checkbox
                      checked={!!row.paid}
                      onChange={() => handlePaidToggle(row)}
                    />
                  </TableCell>

                  <TableCell>
                    <TextField
                      size="small"
                      type="date"
                      value={draft.paid_date ?? ''}
                      onChange={(e) => handleDraftChange(row.id, 'paid_date', e.target.value)}
                      onBlur={() => flushDraft(row, 'paid_date', (v) => v || null)}
                      disabled={!row.paid}
                      sx={{ width: 150 }}
                    />
                  </TableCell>

                  <TableCell sx={{ textAlign: 'center' }}>
                    <Checkbox
                      checked={!!row.invoice_issued}
                      onChange={() => handleInvoiceToggle(row)}
                    />
                  </TableCell>

                  <TableCell>
                    <TextField
                      size="small"
                      placeholder="F-2026-..."
                      value={draft.invoice_number ?? ''}
                      onChange={(e) => handleDraftChange(row.id, 'invoice_number', e.target.value)}
                      onBlur={() => flushDraft(row, 'invoice_number', (v) => v || null)}
                      sx={{ width: 130 }}
                    />
                  </TableCell>

                  <TableCell>
                    <Chip label={st.label} color={st.color} size="small" />
                  </TableCell>

                  <TableCell>
                    <TextField
                      size="small"
                      multiline
                      maxRows={3}
                      placeholder="..."
                      value={draft.notlar ?? ''}
                      onChange={(e) => handleDraftChange(row.id, 'notlar', e.target.value)}
                      onBlur={() => flushDraft(row, 'notlar', (v) => v || null)}
                      sx={{ minWidth: 180 }}
                    />
                  </TableCell>
                </TableRow>
              );
            })}
            {rows.length === 0 && (
              <TableRow>
                <TableCell colSpan={9} align="center" sx={{ color: 'text.secondary', py: 4 }}>
                  Bu dönem için Client firma kaydı yok.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
          {rows.length > 0 && (
            <TableFooter>
              <TableRow sx={{ '& td': { fontWeight: 'bold', backgroundColor: '#f5f5f5' } }}>
                <TableCell>TOPLAM ({rows.length} firma)</TableCell>
                <TableCell sx={{ textAlign: 'right' }}>{fmtTL(kpis.expected)}</TableCell>
                <TableCell colSpan={2} sx={{ textAlign: 'right' }}>Tahsil:</TableCell>
                <TableCell colSpan={2}>{fmtTL(kpis.collected)}</TableCell>
                <TableCell colSpan={3}>Bekleyen: {fmtTL(kpis.pending)}</TableCell>
              </TableRow>
            </TableFooter>
          )}
        </Table>
      </TableContainer>
    </Box>
  );
}

function KpiCard({ title, value, accent }) {
  const colorMap = {
    success: '#2e7d32',
    warning: '#ed6c02',
    error: '#d32f2f',
  };
  return (
    <Card sx={{ flex: 1, borderLeft: `4px solid ${colorMap[accent] || '#1976d2'}` }}>
      <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
        <Typography variant="caption" color="text.secondary">
          {title}
        </Typography>
        <Typography variant="h6" sx={{ fontWeight: 600, mt: 0.5 }}>
          {fmtTL(value)}
        </Typography>
      </CardContent>
    </Card>
  );
}

export default AdminCompanyPayments;
